import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMobileApp } from '../utils/MobileAppContext';
import { ReactComponent as SharePointIcon } from '../assets/sharepoint.svg';
import { ReactComponent as PowerPlatform } from '../assets/PowerPlatform_scalable.svg';
import { ReactComponent as SSecurity } from '../assets/Defender.svg';
import { ReactComponent as CloudService } from '../assets/Cloud_Services.svg';
import { ReactComponent as CDatabase } from '../assets/Cloud_Database.svg';
import { ReactComponent as AIML } from '../assets/AI.svg';
import { ReactComponent as DevOps } from '../assets/DevOps.svg';
import { ReactComponent as CustomDev } from '../assets/CustomDev.svg';
import { ReactComponent as FlutterApp } from '../assets/flutter_app.svg';
import { ReactComponent as UIDesign } from '../assets/UIDesign.svg';
import { ReactComponent as Digitalmarketing } from '../assets/digitalmarketing.svg';

import {
  ArrowRight,
  CheckCircle,
  Box
} from 'lucide-react';

import SEO from '../components/SEO';

const Services: React.FC = () => {
  const { setHighlight, isHighlighted } = useMobileApp();
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'sharepoint',
      icon: <SharePointIcon className="icon-common2" />,
      title: 'SharePoint',
      description: 'On-Prem - 2010/2013/2016/2019, Online.',
      features: ['Setup', 'Migration', 'Custom Development', 'Support'],
      price: ''
    },
    {
      id: 'power-platform',
      icon: <PowerPlatform className="icon-common2" />,
      title: 'Power Platform',
      description: 'Power Apps, Power Platform, Power Pages, Power BI, Copilot Studio',
      features: ['Migration', 'Custom Development', 'Support'],
      price: ''
    },
    {
      id: 'cyber-security',
      icon: <SSecurity className="icon-common2" />,
      title: 'Cyber Security',
      description: 'Cybersecurity Risk Management and assessment, Information Security Management System.',
      features: ['ISO27001 certification preparation and implementation.', 'Third Party risk assessment.', 'Security Policy review and gap analysis.', 'And More..'],
      price: ''
    },
    {
      id: 'custom-web-development',
      icon: <CustomDev className="icon-common2" />,
      title: 'Custom Web Development',
      description: 'Full-stack web applications and custom CMS websites built with WordPress and modern frameworks like React, Angular, and Node.js.',
      features: ['Responsive Design', 'WordPress & CMS Development', 'SEO Optimized', 'Performance Focused', 'Modern Architecture', 'Payment gateway Integration'],
      price: ''
    },
    {
      id: 'mobile-app-development',
      icon: <FlutterApp className="icon-common2" />,
      title: 'Mobile App Development',
      description: 'Native iOS/Android apps and cross-platform solutions using React Native and Flutter.',
      features: ['Cross-Platform', 'Native Performance', 'App Store Ready', 'Push Notifications'],
      price: ''
    },
    {
      id: 'cloud-solutions',
      icon: <CloudService className="icon-common2" />,
      title: 'Cloud Solutions',
      description: 'AWS, Azure infrastructure setup, migration, and optimization.',
      features: ['Auto Scaling', 'Cost Optimization', 'Security Best Practices', '24/7 Monitoring'],
      price: ''
    },
    {
      id: 'ai-ml-solutions',
      icon: <AIML className="icon-common2" />,
      title: 'AI/ML Solutions',
      description: 'Machine learning models, AI integration, and data analytics solutions.',
      features: ['Custom Models', 'Data Pipeline', 'API Integration', 'Real-time Processing'],
      price: ''
    },
    {
      id: 'database-solutions',
      icon: <CDatabase className="icon-common2" />,
      title: 'Database Solutions',
      description: 'Database design, optimization, and migration services for SQL and NoSQL systems.',
      features: ['Performance Tuning', 'Data Migration', 'Backup Strategy', 'Security Implementation'],
      price: ''
    },
    {
      id: 'devops-infrastructure',
      icon: <DevOps className="icon-common2" />,
      title: 'DevOps & Infrastructure',
      description: 'CI/CD pipelines, containerization, and infrastructure automation.',
      features: ['Docker/Kubernetes', 'Automated Deployment', 'Monitoring Setup', 'Infrastructure as Code'],
      price: ''
    },
    {
      id: 'graphics-visualization',
      icon: <UIDesign className="icon-common2" />,
      title: 'Graphics and Visualization',
      description: 'Creating engaging and user-friendly interfaces for a seamless experience.',
      features: ['Graphics & Visual Design', 'UI/UX Design (Web + Mobile App)', 'Logo Branding & Branding guideline', 'Figma Design & Interactive Prototypes', 'Video Editing & Motion Graphics'],
      price: ''
    },
    {
      id: 'performance-marketing',
      icon: <Digitalmarketing className="icon-common2" />,
      title: 'Performance Marketing',
      description: 'Transforming Advertising Budgets into High-Quality Leads without Excessive Expenditure',
      features: ['Audience Exploration', 'Audience Experimentation', 'Creative Assessment', 'Profit and Performance Estimation', 'Expanding & Constructing more lucrative ad campaigns', 'Surpassing your competitive landscape', 'Daily Account Evaluation', 'Weekly Performance Summaries', 'Observations and Subsequent Strategic Measures'],
      price: ''
    },
    {
      id: '3d-scientific-visualization',
      icon: <Box className="icon-common2 text-blue-600" />,
      title: '3D scientific visualization using Babylon.js and Three.js',
      description: 'Creating engaging and user-friendly interfaces for a seamless experience. We specialize in creating interactive 3D visualizations of complex simulations using ThreeJs and Babylon.',
      features: [
        'Creating interactive 3D visualizations of complex simulations',
        'High performance interactive 3D graphics',
        'Simulation of Digital Twins and IoT data',
        'Medical and Industrial simulations using Three JS and meshing libraries',
        'ThreeJs and Babylon for visualizing massive and complex 3D mesh data'
      ],
      price: ''
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Our Services"
        description="Comprehensive software development services including SharePoint, Power Platform, Mobile Apps, Cloud Solutions, and AI/ML."
        keywords="Custom Web Development, SharePoint Migration, Power Apps, Cloud Hosting, AI Solutions, DevOps"
      />
      {/* Hero Section */}
      <section data-id="services-hero" className="bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 data-id="services-hero-title" className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 animate-gradient-x">Services</span>
            </h1>
            <p data-id="services-hero-description" className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Comprehensive software development services tailored to your business needs.
              From concept to deployment, we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-id="services-grid" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <h2 data-id="services-grid-title" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Offer
            </h2>
            <p data-id="services-grid-description" className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional development services across all modern technologies and platforms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              return (
                <div
                  key={index}
                  id={service.id}
                  data-id={`service-card-${index}`}
                  onClick={() => setHighlight(location.pathname, `service-card-${index}`)}
                  className={`relative overflow-hidden bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl border transition-all duration-500 hover:-translate-y-2 group reveal reveal-delay-${(index % 3) + 1} ${
                    isHighlighted(location.pathname, `service-card-${index}`) 
                      ? 'border-blue-500 ring-4 ring-blue-500/30 bg-blue-50/20' 
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  {/* Top Animated Gradient Bar */}
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                  
                  <div className="flex text-4xl mb-3 transform group-hover:scale-105 transition-transform duration-300 origin-left">
                    {service.icon} <h3 className="text-xl font-bold text-gray-900 mb-3 ml-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                  </div>

                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-blue-600">{service.price}</span>
                    <Link
                      to="/contact"
                      className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/30 active:scale-95 group/btn"
                    >
                      Get Quote
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-id="services-cta" className="py-8 bg-gradient-to-r from-blue-900 to-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 data-id="services-cta-title" className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need a Custom Solution?
          </h2>
          <p data-id="services-cta-description" className="text-xl text-blue-200 mb-8 max-w-3xl mx-auto">
            Every project is unique. Let's discuss your specific requirements and create a tailored solution that fits your needs and budget.
          </p>
          <Link
            to="/contact"
            data-id="services-cta-btn"
            className="relative inline-flex items-center px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl group"
          >
            {/* Radiation Effect */}
            <div className="absolute inset-0 rounded-lg border-2 border-white opacity-50 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] -z-10 bg-white shadow-xl shadow-white/50"></div>
            <div className="absolute inset-0 rounded-lg border-2 border-white opacity-40 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_1s] -z-10"></div>
            
            <span>Get Free Consultation</span>
            <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;